import React, { useState, useEffect, useMemo, useCallback, useContext, forwardRef } from 'react'

import { MapContext, Polyline } from '@react-google-maps/api'
import PropTypes from 'prop-types'

import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import round from 'lodash/round'
import upperFirst from 'lodash/upperFirst'

import { Button, IconButton } from '@material-ui/core'
import {
  LockOpen as LockOpenIcon,
  LockOutlined as LockIcon,
  DeleteOutlined as DeleteOutlinedIcon
} from '@material-ui/icons'

import { useT } from '@galo-monitor/i18n'
import colors from '@galo-monitor/styles/colors'
import { getPolygonArea } from '@galo-monitor/utils/maps'
import Card from '@galo-monitor/web-components/Card'
import PopupPolygon from '@galo-monitor/web-components/Maps/Google/components/PopupPolygon'
import Tooltip from '@galo-monitor/web-components/Tooltip'

const DEFAULT_POLYGON_OPTIONS = {
  fillColor: colors.primary,
  strokeColor: colors.primary,
  fillOpacity: 0.2,
  strokeOpacity: 0.8,
  strokeWeight: 2
}

const DrawingManager = forwardRef(({ convertUnit, overlayData, onToggleLockMap }, overlayRef) => {
  const t = useT()
  const [overlayPath, setOverlayPath] = useState(overlayData)
  const [editPolygon, setEditPolygon] = useState(false)

  const googleMap = useContext(MapContext)
  const [draftPath, setDraftPath] = useState([])
  const isDrawing = editPolygon && isEmpty(overlayPath)

  useEffect(() => {
    if (!googleMap || !isDrawing) return undefined

    const previousCursor = googleMap.get('draggableCursor')
    googleMap.setOptions({ draggableCursor: 'crosshair' })
    const listener = googleMap.addListener('click', event => {
      if (event.latLng) {
        const point = { lat: event.latLng.lat(), lng: event.latLng.lng() }
        setDraftPath(path => [...path, point])
      }
    })

    return () => {
      listener.remove()
      googleMap.setOptions({ draggableCursor: previousCursor })
    }
  }, [googleMap, isDrawing])

  useEffect(() => {
    if (!isDrawing) setDraftPath([])
  }, [isDrawing])

  const polygonArea = useMemo(
    () => {
      const coordinates = map(
        overlayPath,
        ({ lat: latitude, lng: longitude }) => ({ latitude, longitude })
      )

      return {
        [convertUnit]: round(getPolygonArea(coordinates, convertUnit), 2),
        m2: round(getPolygonArea(coordinates), 2)
      }
    },
    [convertUnit, overlayPath]
  )

  const toggleEditPolygon = useCallback(() => {
    setEditPolygon(prevState => !prevState)
    onToggleLockMap()
  }, [onToggleLockMap])

  const customButtons = useMemo(() => [
    {
      key: 'lock-map-btn',
      'aria-label': 'Lock map',
      onClick: toggleEditPolygon,
      tooltipText: t('lock map'),
      enabled: true,
      children: editPolygon
        ? <LockOpenIcon />
        : <LockIcon />
    },
    {
      key: 'delete-polygon-btn',
      'aria-label': 'Detele polygon',
      onClick: () => setOverlayPath([]),
      tooltipText: `${ upperFirst(t('delete')) } ${ t('polygon', { howMany: 1 }) }`,
      enabled: !isEmpty(overlayPath),
      children: <DeleteOutlinedIcon />
    }
  ], [editPolygon, overlayPath, t, toggleEditPolygon])

  // Updates polygon paths
  const getListPosition = useCallback(() => {
    const polygon = overlayRef.current && overlayRef.current.polygon
    if (!polygon) return

    setOverlayPath(polygon.getPath().getArray().map(latLng => ({
      lat: latLng.lat(),
      lng: latLng.lng()
    })))
  }, [overlayRef])

  useEffect(() => {
    setOverlayPath(overlayData)
    setDraftPath([])
  }, [overlayData])

  return (
    <div style={ { position: 'absolute', top: 0, right: 0, zIndex: 2, maxWidth: '100%' } }>
      {isDrawing ? (
        <>
          <Polyline
            path={ draftPath }
            options={ { strokeColor: colors.primary, clickable: false } }
          />
          <Card noWrapper cardStyle={ { padding: 8, margin: 10 } } headerStyle={ { display: 'none' } }>
            <div>{t('polygon drawing instructions')}</div>
            <Button
              disabled={ draftPath.length < 3 }
              onClick={ () => setOverlayPath(draftPath) }
            >
              {t('finish polygon drawing')}
            </Button>
            <Button
              disabled={ !draftPath.length }
              onClick={ () => setDraftPath(path => path.slice(0, -1)) }
            >
              {t('undo polygon point')}
            </Button>
          </Card>
        </>
      ) : null}
      <PopupPolygon
        ref={ overlayRef }
        onMouseUp={ getListPosition }
        options={ { ...DEFAULT_POLYGON_OPTIONS, editable: editPolygon } }
        paths={ overlayPath }
        area={ polygonArea }
      >
        <div>{`${ t('area', { howMany: 1 }) }: ${ polygonArea[convertUnit] } ${ convertUnit } (${ polygonArea.m2 } m²)`}</div>
      </PopupPolygon>
      {
        map(customButtons, ({
          children,
          key,
          tooltipText,
          enabled,
          ...buttonProps
        }) => enabled && (
          <Card
            key={ key }
            cardStyle={ {
              zIndex: 2,
              borderRadius: 2,
              padding: 0,
              margin: '10px 10px 0 0'
            } }
            headerStyle={ { display: 'none' } }
            style={ { margin: 0, padding: 0 } }
          >
            <Tooltip title={ tooltipText }>
              <IconButton
                { ...buttonProps }
                style={ { padding: 8 } }
              >
                {children}
              </IconButton>
            </Tooltip>
          </Card>
        ))
      }
    </div>
  )
})

DrawingManager.propTypes = {
  convertUnit: PropTypes.string,
  overlayData: PropTypes.array,
  onToggleLockMap: PropTypes.func
}

DrawingManager.defaultProps = {
  convertUnit: 'm2',
  overlayData: [],
  onToggleLockMap: () => {}
}

export default DrawingManager
